import dayjs, { Dayjs } from "dayjs";

import { AgentAvailableContactSlotsResponseAvailableContactSlots } from "@/api/generated";

// 希望連絡日程の時間帯。2 時間刻みの 6 枠（旧 morning / afternoon / evening は保存済みデータの表示専用で、新規送信では使わない）
export type PreferredContactTimeSlot =
  | "slot_9_11"
  | "slot_11_13"
  | "slot_13_15"
  | "slot_15_17"
  | "slot_17_19"
  | "slot_19_21";

export interface PreferredContactTimeSlotOption {
  value: PreferredContactTimeSlot;
  label: string;
  // 「当日の過ぎた枠は出さない」判定に使う開始時刻（時）
  startHour: number;
}

export const PREFERRED_CONTACT_TIME_SLOT_OPTIONS: readonly PreferredContactTimeSlotOption[] = [
  { value: "slot_9_11", label: "9:00-11:00", startHour: 9 },
  { value: "slot_11_13", label: "11:00-13:00", startHour: 11 },
  { value: "slot_13_15", label: "13:00-15:00", startHour: 13 },
  { value: "slot_15_17", label: "15:00-17:00", startHour: 15 },
  { value: "slot_17_19", label: "17:00-19:00", startHour: 17 },
  { value: "slot_19_21", label: "19:00-21:00", startHour: 19 },
];

// 表示用のラベル辞書。旧 3 枠で保存済みの提案も表示できるよう、旧値も残す
export const PREFERRED_CONTACT_TIME_SLOT_LABEL_MAP: Record<string, string> = {
  morning: "午前 (9:00-12:00)",
  afternoon: "午後 (13:00-17:00)",
  evening: "夕方 (17:00-20:00)",
  ...PREFERRED_CONTACT_TIME_SLOT_OPTIONS.reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {}),
};

const WEEK_DAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

export const formatPreferredContactDate = (date: string): string => {
  const parsedDate = dayjs(date);
  return `${parsedDate.format("M月D日")} (${WEEK_DAY_LABELS[parsedDate.day()]})`;
};

export interface PreferredContactDateOption {
  // API へ送る日付（YYYY-MM-DD）
  value: string;
  label: string;
  // その日に選べる時間帯。空になる日付は候補から除外するため、常に 1 件以上入る
  timeSlots: PreferredContactTimeSlotOption[];
}

// 希望連絡日程の選択肢（今日から days 日分）を組み立てる。
// - agentAvailableContactSlots が空 or 未指定なら「全枠対応可」として扱う（fail-open）。
//   日程入力自体は任意でスキップ可能だが、取得失敗で候補がゼロになると入力しようとした
//   ユーザーが 1 枠も選べなくなり体験として不成立になるため、これを避けるのが最優先
// - 当日は開始時刻を過ぎた枠を出さない
// - 選べる枠が 1 つも無い日付は候補ごと落とす
export const buildPreferredContactDateOptions = (
  now: Dayjs,
  agentAvailableContactSlots?: AgentAvailableContactSlotsResponseAvailableContactSlots[] | null,
  days = 7
): PreferredContactDateOption[] => {
  const currentMinutes = now.hour() * 60 + now.minute();
  const today = now.format("YYYY-MM-DD");

  const build = (slots?: AgentAvailableContactSlotsResponseAvailableContactSlots[] | null) =>
    Array.from({ length: days }, (_, index) => now.add(index, "day"))
      .map((date) => {
        const value = date.format("YYYY-MM-DD");
        const timeSlots = PREFERRED_CONTACT_TIME_SLOT_OPTIONS.filter((option) => {
          if (value === today && currentMinutes >= option.startHour * 60) return false;
          if (!slots || slots.length === 0) return true;
          return slots.some((slot) => slot.wday === date.day() && slot.time_slot === option.value);
        });

        return { value, label: formatPreferredContactDate(value), timeSlots };
      })
      .filter((option) => option.timeSlots.length > 0);

  const restricted = build(agentAvailableContactSlots);
  // 対応可能枠が「今日から days 日の窓に 1 度も現れない」設定（例: 木曜のみ対応で、今日の木曜の枠は
  // すでに過ぎている）だと候補がゼロになり、入力しようとしたユーザーが選択肢を選べなくなる。
  // その場合も制限なしへ倒す（fail-open）
  return restricted.length > 0 ? restricted : build(undefined);
};

// 指定日付で選べる時間帯を返す（該当日付が候補に無ければ空配列）
export const findPreferredContactTimeSlotOptions = (
  dateOptions: PreferredContactDateOption[],
  date: string
): PreferredContactTimeSlotOption[] => dateOptions.find((option) => option.value === date)?.timeSlots ?? [];
